import { useEffect, useState, useMemo, useCallback } from 'react';
import { eventPageColumns, donatePageColumns } from 'constants/tables';
import { PagesTable } from 'components/CustomTable/PagesTable';
import { inputDate, tableDate } from 'helpers/date';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getPagesState } from 'selectors/pages';
import { getSubscriptionLevel } from 'selectors/partners';

import { useAdminAuth } from 'hooks/useAdminAuth';
import { getPageActions, getPagePreviewUrl, useHandlePageUpdate } from './utils';
import { getAllPages, getPageDetail, getPages, pageCleanUp, updatePage } from 'store/pages/actions';
import { showToast } from 'components/Toast';
import { DashboardScreen, trackEvent, trackPageRowActionClicked } from 'helpers/mixpanelEvents';

const SCREEN: DashboardScreen = 'pages';

const Pages = ({ category }) => {
  const dispatch = useAppDispatch();
  const [popupType, setPopupType] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setDeleting] = useState(false);

  const [filterSelected, setFilterSelected] = useState('all');
  const baseHandleUpdate = useHandlePageUpdate(category);
  const handleUpdate = useCallback(
    (type: string, param: string, pageId?: string) => {
      if (type === 'delete') {
        trackPageRowActionClicked({
          screen: SCREEN,
          page_category: category,
          action: type,
          page_id: String(param),
        });
        setPopupType(`delete-${param}`);
        setIsOpen(true);
        return;
      }
      baseHandleUpdate(type, param, pageId);
    },
    [baseHandleUpdate, category]
  );

  const { clientId, activeRole, isSuper } = useAdminAuth();
  const pagesState = useAppSelector(getPagesState);

  const subscriptionLevel = useAppSelector(getSubscriptionLevel);

  const closePopup = () => {
    setIsOpen(false);
    setPopupType('');
  };

  useEffect(() => {
    dispatch(pageCleanUp());
  }, []);

  useEffect(() => {
    // Only call getAllPages for super-admins
    if (!isSuper) return;
    if (!pagesState.isLoading && !pagesState.data.allPages) {
      dispatch(getAllPages());
    }
  }, [clientId, isSuper]);

  const pageColumns = useMemo(() => {
    return category === 'event' ? eventPageColumns : donatePageColumns;
  }, [category]);

  useEffect(() => {
    // 0 is super's ALL (lists every client's pages); only null means no selection resolved yet.
    if (clientId == null || !activeRole) return;
    dispatch(getPages(clientId, category, activeRole));
  }, [category, clientId, activeRole]);

  const dataState = useMemo(() => {
    if (!pagesState.isLoading && pagesState.data?.clientPages) {
      return pagesState.data.clientPages?.pages
        ?.filter(
          page => page.is_enabled && (filterSelected === 'all' || page.status === filterSelected)
        )
        ?.map((prop, key) => {
          return {
            id: key,
            pageName: prop.value,
            webUrl: getPagePreviewUrl(category, prop.query_str),
            updatedDate: inputDate(new Date(prop.updated_date).getTime()),
            eventDate: tableDate(prop?.start_datetime),
            status: prop.status,
            actions: getPageActions(category, activeRole, handleUpdate, prop, subscriptionLevel),
          };
        });
    }
    return [];
  }, [pagesState.data?.clientPages, filterSelected, category, handleUpdate]);

  useEffect(() => {
    if (!pagesState.isLoading && pagesState.data) {
      if (pagesState.data?.pageCreated) {
        if (activeRole) {
          dispatch(getPages(clientId, category, activeRole));
        }
        dispatch(pageCleanUp());
      }
      if (pagesState.data.pageDetail && isDeleting) {
        setDeleting(false);
        const pageBody = category === 'event' ? 'events' : 'pages';
        const pageData = {
          ...pagesState.data.pageDetail[pageBody][0],
          is_enabled: false,
        };

        const updateBody = {
          client_id: clientId,
          ...(category === 'event' ? pageData : { page: pageData }),
        };
        dispatch(updatePage(updateBody, category, activeRole));
        closePopup();
      }
    }
  }, [pagesState, isDeleting, category]);

  const handleDeletePage = () => {
    if (!clientId) {
      showToast('A client must be selected to delete a page', 'error');
      closePopup();
      return;
    }
    const tagID = parseInt(popupType.split('-')[1], 10);
    trackEvent('Delete Page Confirmed', {
      screen: SCREEN,
      page_category: category,
      page_id: String(tagID),
    });
    const selected = pagesState?.data?.clientPages?.pages?.find(page => page.id === tagID);
    dispatch(getPageDetail(selected?.query_str || '', category, activeRole));
    setDeleting(true);
  };

  return (
    <PagesTable
      data={dataState}
      category={category}
      isLoading={pagesState.isLoading}
      popupType={popupType}
      setPopupType={setPopupType}
      pageColumns={pageColumns}
      subscriptionLevel={subscriptionLevel}
      handleDeletePage={handleDeletePage}
      closePopup={closePopup}
      setFilterSelected={setFilterSelected}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  );
};

export default Pages;
